import { UserContextRootState } from 'core/userContext';
import {
  selectIsCatalogTriggerValuesOn,
  selectIsMultiSelectInputComparatorsFeatureFlagOn,
  selectIsUseRegexInSearchQueryFeatureFlagOn,
} from 'core/userContext/selectors';
import { getCatalogFieldTypesIsLoading } from 'modules/catalogFieldTypes/selectors';
import {
  changeComparator,
  changeField,
  clearValue,
  copyCriteriaProperty,
  deleteCriteriaProperty,
  toggleDynamic,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import {
  selectCanDeleteCriteriaProperty,
  selectComparator,
  selectComparatorDropdownProps,
  selectEsIndex,
  selectField,
  selectFieldType,
  selectFieldsForProperty,
  selectIsClonedData,
  selectIsViewMode,
  selectSearchQueryCombinator,
  selectShowDynamicFieldToggle,
  selectShowValidations,
  selectValueType,
} from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { RouterState } from 'redux-first-history';

import * as c from 'utils/comparatorTypes';
import * as t from 'utils/fieldTypes';
import noop from 'utils/noop';
import ActionButtons from '../actionButtons';
import ComparatorDropdown from '../comparatorDropdown';
import CriteriaPropertyField from '../criteriaPropertyField';
import FieldDropdown from '../fieldDropdown';
import { FieldTextInput } from '../fieldTextInput';
import { Field } from '../fieldTypeaheadSelect/types';
import ValueTypeDropdown from '../valueTypeDropdown';
import { FieldContainer, Label, MainContainer } from './CriteriarPropertyGroup.styled';
import { getValueTypeDropdownValue } from './utils';
import { QueryBuilderIndex } from '../../types';

type CriteriaPropertyGroupProps = {
  /** The animation time in milliseconds. */
  animationTime: number;
  /** If the delete button should show. */
  canDelete: boolean;
  /** The combinator value of the search query. */
  combinator: string;
  /** The comparator value for the criteria property. */
  comparator: string;
  /** Additional props for the comparator dropdown. Mainly used for passing in custom comparator types. */
  comparatorDropdownProps: Record<string, unknown>;
  /** The index of the criteria property. */
  criteriaPropertyIndex: number;
  /** The queryBuilders esIndex */
  esIndex: QueryBuilderIndex;
  /** The field value of the criteria property. */
  field: string;
  /** The list of fields for the field dropdown. */
  fields: Field[];
  /** The field type of the criteria property. */
  fieldType: string;
  /** Flag if the data is cloned instead of actually set on the journey */
  isClonedData: boolean;
  /** Flag if CriteriaTriggerValues flag is on. */
  isCriteriaTriggerValuesEnabled: boolean;
  /** Flag whether field types are loading */
  isFieldTypesLoading: boolean;
  /** Flag if regex comparators are allowed  */
  isRegexUsageAllowed: boolean;
  /** Value type of a field. */
  valueType: string;
  /** If query builder is in view mode. */
  isViewMode: boolean;
  /** If query builder is in read only mode. */
  isReadOnly?: boolean;
  /** If MultiSelectInputComparators FF is on */
  isMultiSelectInputAllowed?: boolean;
  /** Values of the query, if there are multiple */
  values?: any[];
  /** Disables animations. */
  noAnimation: boolean;
  /**
   * Callback function when comparator value changes.
   *
   * ```
   * onChangeComparator(value, searchGroupIndex, searchQueryIndex, criteriaPropertyIndex) => {}
   * ```
   * @param { String } value: The new comparator value.
   * @param { Number } searchGroupIndex: The index of the search group the comparator is in.
   * @param { Number } searchQueryIndex: The index of the search query the comparator is in.
   * @param { Number } criteriaPropertyIndex: The index of the criteria property the comparator is in.
   * @returns { void }
   */
  onChangeComparator: (
    value: string,
    searchGroupIndex: number,
    searchQueryIndex: number,
    criteriaPropertyIndex: number,
    valueType: string,
  ) => void;
  /**
   * Callback function when field value changes. Updates field and field type.
   *
   * ```
   * onChangeField(field, fieldType, searchGroupIndex, searchQueryIndex, criteriaPropertyIndex) => {}
   * ```
   * @param { String } field: The new field value.
   * @param { String } fieldType: The new field type value.
   * @param { String } valueType: The value type. Possibly a dupe of fieldType that's made its way into the code.
   * @param { Number } searchGroupIndex: The index of the search group the field is in.
   * @param { Number } searchQueryIndex: The index of the search query the field is in.
   * @param { Number } criteriaPropertyIndex: The index of the criteria property the field is in.
   * @returns { void }
   */
  onChangeField: (
    field: string,
    fieldType: string,
    valueType: string,
    searchGroupIndex: number,
    searchQueryIndex: number,
    criteriaPropertyIndex: number,
  ) => void;

  onClearValue: (
    searchGroupIndex: number,
    searchQueryIndex: number,
    criteriaPropertyIndex: number,
    value: string,
  ) => void;
  /**
   * Callback function when copying a criteria property.
   *
   * ```
   * onCopyCriteriaProperty(criteriaPropertyIndex, searchGroupIndex, searchQueryIndex) => {}
   * ```
   * @param { Number } criteriaPropertyIndex: The index of the criteria property to copy.
   * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
   * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
   * @returns { void }
   */
  onCopyCriteriaProperty: (
    criteriaPropertyIndex: number,
    searchGroupIndex: number,
    searchQueryIndex: number,
    valueType: string,
  ) => void;
  /**
   * Callback function when deleting a criteria property.
   *
   * ```
   * onDeleteCriteriaProperty(criteriaPropertyIndex, searchGroupIndex, searchQueryIndex) => {}
   * ```
   * @param { Number } criteriaPropertyIndex: The index of the criteria property to delete.
   * @param { Number } searchGroupIndex: The index of the search group the criteria property is in.
   * @param { Number } searchQueryIndex: The index of the search query the criteria property is in.
   * @returns { void }
   */
  onDeleteCriteriaProperty: (
    criteriaPropertyIndex: number,
    searchGroupIndex: number,
    searchQueryIndex: number,
  ) => void;
  /**
   * Callback function when dynamic field toggle value changes.
   *
   * ```
   * onToggleDynamic(valueType, searchGroupIndex, searchQueryIndex, criteriaPropertyIndex) => {}
   * ```
   * @param { String } valueType: If the field is dynamic.
   * @param { Number } searchGroupIndex: The index of the search group the field is in.
   * @param { Number } searchQueryIndex: The index of the search query the field is in.
   * @param { Number } criteriaPropertyIndex: The index of the criteria property the field is in.
   * @returns { void }
   */
  onToggleDynamic: (
    valueType: string,
    searchGroupIndex: number,
    searchQueryIndex: number,
    criteriaPropertyIndex: number,
  ) => void;
  /** The index of the search group the criteria property is in. */
  searchGroupIndex: number;
  /** The index of the search query the criteria property is in. */
  searchQueryIndex: number;
  /** If validation should show on fields */
  shouldValidateFields?: boolean;
  /** Show the dynamic field toggle. */
  showDynamicFieldToggle: boolean;
  /** Show the field text input for fields. */
  showFieldTextInput?: boolean;
  /** If validations are shown. */
  showValidations: boolean;
  /** Store object to get state. (Used for connected CriteriaPropertyField component) */
  store: Record<string, unknown>;
  /** Whether or not to display a cast type field for specific Long type criteria property fields */
  useCastTypeField: boolean;
  /** Consumer of the query builder */
  type: string;
};

type CriteriaPropertyGroupState = {
  animationTime: number;
  isDeleting: boolean;
  fieldIsInFields: boolean;
};

class CriteriaPropertyGroup extends React.Component<CriteriaPropertyGroupProps> {
  static defaultProps = {
    animationTime: 200,
    canDelete: false,
    comparator: '',
    comparators: {},
    isViewMode: false,
    isReadOnly: false,
    noAnimation: false,
    onChangeComparator: noop,
    onChangeField: noop,
    onCopyCriteriaProperty: noop,
    onDeleteCriteriaProperty: noop,
    onToggleDynamic: noop,
    showValidations: false,
  };

  constructor(props: CriteriaPropertyGroupProps) {
    super(props);
    const { noAnimation, animationTime } = props;

    this.state = {
      animationTime: noAnimation ? 0 : animationTime,
      isDeleting: false,
      fieldIsInFields: true,
    } as CriteriaPropertyGroupState;
  }

  // both the DidMount and DidUpdate here check that the field is available in fields, a check performed for journey cloning wor
  componentDidMount(): void {
    const { isClonedData, fields, field, shouldValidateFields } = this.props;
    if ((isClonedData || shouldValidateFields) && field) {
      this.setState({ ...this.state, fieldIsInFields: fields?.some((f) => f.value === field) });
    }
  }

  componentDidUpdate(prevProps: Readonly<CriteriaPropertyGroupProps>): void {
    const { isClonedData, fields, field, shouldValidateFields } = this.props;
    const hasFieldChanged = field !== prevProps.field;
    const hasFieldValidationChanged = shouldValidateFields !== prevProps.shouldValidateFields;

    if ((isClonedData || shouldValidateFields) && hasFieldChanged) {
      this.setState({ ...this.state, fieldIsInFields: fields?.some((f) => f.value === field) });
    }
    // This resets fields whenever we toggle back to "All events"
    if (!shouldValidateFields && hasFieldValidationChanged) {
      this.setState({ ...this.state, fieldIsInFields: true });
    }
  }

  /**
   * Returns the value type for the value field.
   *
   * TODO: When adding trigger values, add to this function to return v.TRIGGER if value is a trigger value.
   *
   * @returns { String } The value type,
   */
  getValueTypeDropdownValue = () => getValueTypeDropdownValue(this.props);

  /**
   * Callback function when the comparator changes.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the new comparator value.
   * @returns { void }
   */
  // e is unused, refactoring it out is actually hard
  handleChangeComparator = (e: any, { value }: { value: string }) => {
    const {
      criteriaPropertyIndex,
      valueType,
      onChangeComparator,
      searchGroupIndex,
      searchQueryIndex,
    } = this.props;
    onChangeComparator(value, searchGroupIndex, searchQueryIndex, criteriaPropertyIndex, valueType);
  };

  /**
   * Callback function when field changes.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the new type and field values.
   * @returns { void }
   */
  // e is unused, refactoring it out is actually hard
  handleChangeField = (e: any, { type, value }: { type: string; value: string }) => {
    const { field } = this.props;
    const {
      criteriaPropertyIndex,
      valueType,
      onChangeField,
      onClearValue,
      searchGroupIndex,
      searchQueryIndex,
    } = this.props;
    onChangeField(
      value,
      type,
      valueType,
      searchGroupIndex,
      searchQueryIndex,
      criteriaPropertyIndex,
    );
    if (field !== '') {
      onClearValue(searchGroupIndex, searchQueryIndex, criteriaPropertyIndex, value);
    }
  };

  /**
   * handleToggleDynamic
   * Callback function when dynamic field is toggled.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: Upadted props containing updated value of valueType.
   * @returns { void }
   */
  // e is unused, refactoring it out is actually hard
  handleToggleDynamic = (e: any, { valueType }: { valueType: string }) => {
    const { criteriaPropertyIndex, onToggleDynamic, searchGroupIndex, searchQueryIndex } =
      this.props;
    onToggleDynamic(valueType, searchGroupIndex, searchQueryIndex, criteriaPropertyIndex);
  };

  /**
   * handleCopy
   * Callback function when copy button is clicked.
   * @returns { void }
   */
  handleCopy = () => {
    const {
      criteriaPropertyIndex,
      valueType,
      onCopyCriteriaProperty,
      searchGroupIndex,
      searchQueryIndex,
    } = this.props;
    onCopyCriteriaProperty(criteriaPropertyIndex, searchGroupIndex, searchQueryIndex, valueType);
  };

  /**
   * postDelete
   * Callback function after delete action has been called. Calls onDeleteCriteriaProperty.
   * @returns { void }
   */
  postDelete = () => {
    const { criteriaPropertyIndex, onDeleteCriteriaProperty, searchGroupIndex, searchQueryIndex } =
      this.props;
    this.setState({ isDeleting: false });
    onDeleteCriteriaProperty(criteriaPropertyIndex, searchGroupIndex, searchQueryIndex);
  };

  /**
   * handleDelete
   * Callback function when delete button is clicked. Sets isDeleting state flag for delete animation.
   * @returns { void }
   */
  handleDelete = () => {
    const { animationTime } = this.state as CriteriaPropertyGroupState;
    this.setState({ isDeleting: true });
    setTimeout(this.postDelete, animationTime);
  };

  /**
   * renderCombinatorLabel
   * Determines the combinator label.
   * @param { Boolean } isViewMode: If query builder is in view mode.
   * @returns { void }
   */
  renderCombinatorLabel = (isViewMode: boolean) => {
    const { combinator, criteriaPropertyIndex } = this.props;
    const labelText = criteriaPropertyIndex === 0 ? 'where' : combinator;
    return <Label isViewMode={isViewMode}>{labelText}</Label>;
  };

  render() {
    const {
      canDelete,
      comparator,
      comparatorDropdownProps,
      criteriaPropertyIndex,
      field,
      fieldType,
      fields,
      isCriteriaTriggerValuesEnabled,
      isFieldTypesLoading,
      isMultiSelectInputAllowed,
      isRegexUsageAllowed,
      isViewMode,
      isReadOnly = false,
      searchGroupIndex,
      searchQueryIndex,
      showDynamicFieldToggle,
      showValidations,
      store,
      useCastTypeField,
      values,
      isClonedData,
      showFieldTextInput,
      type,
      esIndex,
      valueType,
    } = this.props as CriteriaPropertyGroupProps;
    const { fieldIsInFields } = this.state as CriteriaPropertyGroupState;
    const isSetComparatorSelected = [c.IS_SET, c.IS_NOT_SET].includes(comparator);

    return (
      <MainContainer
        isViewMode={isViewMode}
        isGeoLocationField={fieldType === t.GEO_LOCATION}
        data-test="criteria-property-group"
      >
        {this.renderCombinatorLabel(isViewMode)}
        <FieldContainer>
          {showFieldTextInput ? (
            <FieldTextInput
              placeholder="Enter a value"
              value={field}
              onChange={this.handleChangeField}
              error={false} // this is currently a freeform field, so we don't want to show errors
              readOnly={isReadOnly}
              dataTest="querybuilder-field-text-input"
            />
          ) : (
            <FieldDropdown
              className="cssCriteriaPropertyGroupFieldDropdown"
              classNameReadOnly="cssCriteriaPropertyGroupFieldViewModeValue"
              error={!fieldIsInFields || (showValidations && !field)}
              fields={fields}
              placeholder={!fieldIsInFields ? field : undefined}
              onChange={this.handleChangeField}
              type={fieldType}
              value={isClonedData ? (fieldIsInFields && field) || '' : field}
              isLoading={isFieldTypesLoading}
              dataTest="collections-catalog-field-dropdown"
              isReadOnly={isReadOnly}
            />
          )}
          <ComparatorDropdown
            disabled={!field || isReadOnly}
            error={isClonedData || (showValidations && !comparator)}
            field={field}
            onChange={this.handleChangeComparator}
            type={fieldType}
            value={comparator}
            values={values}
            esIndex={esIndex}
            valueType={valueType}
            isMultiSelectInputAllowed={isMultiSelectInputAllowed}
            isRegexUsageAllowed={isRegexUsageAllowed}
            {...comparatorDropdownProps}
          />
          {showDynamicFieldToggle && !isViewMode && (
            <ValueTypeDropdown
              className="cssCriteriaPropertyGroupValueTypeDropdown"
              disabled={isSetComparatorSelected || isReadOnly}
              showContactProperty
              onChange={this.handleToggleDynamic}
              value={this.getValueTypeDropdownValue()}
              showTriggerProperty={isCriteriaTriggerValuesEnabled}
            />
          )}
          <CriteriaPropertyField
            data-test="criteria-property-field"
            type={type}
            criteriaPropertyIndex={criteriaPropertyIndex}
            disabled={isSetComparatorSelected}
            isReadOnly={isReadOnly}
            searchGroupIndex={searchGroupIndex}
            searchQueryIndex={searchQueryIndex}
            store={store}
            useCastTypeField={useCastTypeField}
            field={field}
            values={values}
          />
        </FieldContainer>
        {!isViewMode && !isReadOnly && (
          <ActionButtons
            buttonClassName="cssCriteriaPropertyGroupActionActionButton"
            className="cssCriteriaPropertyGroupActionButtons"
            copyText="Copy Field"
            deleteText="Delete Field"
            onCopy={this.handleCopy}
            onDelete={this.handleDelete}
            canDelete={canDelete}
            showBorders
            trashIconDelete
          />
        )}
      </MainContainer>
    );
  }
}

export { CriteriaPropertyGroup as CriteriaPropertyGroupComponent };

const mapStateToProps = (
  state: Record<string, unknown> & UserContextRootState & { router: RouterState },
  props: Record<string, unknown>,
) => ({
  canDelete: selectCanDeleteCriteriaProperty(state, props),
  combinator: selectSearchQueryCombinator(state, props),
  comparator: selectComparator(state, props),
  comparatorDropdownProps: selectComparatorDropdownProps(state),
  esIndex: selectEsIndex(state),
  valueType: selectValueType(state, props),
  isViewMode: selectIsViewMode(state),
  isMultiSelectInputAllowed: selectIsMultiSelectInputComparatorsFeatureFlagOn(state),
  isRegexUsageAllowed: selectIsUseRegexInSearchQueryFeatureFlagOn(state),
  field: selectField(state, props),
  isClonedData: selectIsClonedData(state, props),
  fields: selectFieldsForProperty(state, props),
  isFieldTypesLoading: getCatalogFieldTypesIsLoading(state),
  fieldType: selectFieldType(state, props),
  showDynamicFieldToggle: selectShowDynamicFieldToggle(state),
  showValidations: selectShowValidations(state),
  isCriteriaTriggerValuesEnabled: selectIsCatalogTriggerValuesOn(state),
});
const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      onChangeComparator: changeComparator,
      onChangeField: changeField,
      onClearValue: clearValue,
      onChangeCriteriaProperty: changeComparator,
      onCopyCriteriaProperty: copyCriteriaProperty,
      onDeleteCriteriaProperty: deleteCriteriaProperty,
      onToggleDynamic: toggleDynamic,
    },
    dispatch,
  );

// @ts-ignore, it doesn't like defaultProps and the usual fixes aren't working.
export default connect(mapStateToProps, mapDispatchToProps)(CriteriaPropertyGroup);
